import React from 'react';
import { Option, Question } from '../../types';
import { useLanguage } from '../../LanguageContext';
import { getValidationMessage } from '../../utils/validationMessages';

interface FormCheckboxProps {
  question: Question;
  handleChange: (questionKey: string, answer: Option[]) => void;
  errors: Record<string, string>;
  setErrors: (value: Record<string, string>) => void;
  disabled: boolean;
}

const FormCheckbox: React.FC<FormCheckboxProps> = ({
  question,
  handleChange,
  errors,
  setErrors,
  disabled,
}) => {
  const { language } = useLanguage();
  const getCurrentAnswers = () => question.answer ?? [];

  const handleCheckboxBlur = (option: Option, checked: boolean) => {
    const currentAnswers = getCurrentAnswers();

    let updatedAnswers: Option[];

    if (option.exclusive && checked) {
      updatedAnswers = [{ guid: option.guid, text: option.text }];
    } else {
      updatedAnswers = checked
        ? [...currentAnswers, { guid: option.guid, text: option.text }]
        : currentAnswers.filter((answer: Option) => answer.guid !== option.guid);
    }

    let errorMessage = '';

    if (updatedAnswers.length > 0) {
      if (question.min_value !== undefined && updatedAnswers.length < question.min_value) {
        errorMessage = getValidationMessage(language, 'select_min_options', {
          min: question.min_value,
        });
      }
    }

    if (errorMessage) {
      setErrors({ ...errors, [question.question_key]: errorMessage });
    } else {
      const next = { ...errors };
      delete next[question.question_key];
      setErrors(next);
    }

    handleChange(question.question_key, updatedAnswers);
  };

  const currentAnswers = getCurrentAnswers();
  const isMaxReached =
    question.max_value !== undefined && currentAnswers.length >= question.max_value;

  const exclusiveOption = question.options?.find(
    (option) => option.exclusive && currentAnswers.some((answer) => answer.guid === option.guid),
  );

  return (
    <div>
      {question.options
        ?.filter((option) => option.active !== false)
        .map((option) => {
          const isChecked = currentAnswers.some((answer) => answer.guid === option.guid);
          const isDisabled = disabled || (exclusiveOption && option.guid !== exclusiveOption.guid);

          return (
            <label key={option.guid} style={{ display: 'block' }}>
              <input
                id={question.question_key}
                type='checkbox'
                onChange={(e) => handleCheckboxBlur(option, e.target.checked)}
                checked={isChecked}
                disabled={isDisabled || (!isChecked && isMaxReached)}
              />
              {String(option.text)}
            </label>
          );
        })}
      {errors[question.question_key] && (
        <span style={{ color: 'red' }}>{errors[question.question_key]}</span>
      )}
    </div>
  );
};

export default FormCheckbox;
